<script setup lang="ts">
import { Button, Input, Label, ZhPanel, ZhSectionHeading } from '@zerohuman/ui'
import { computed, ref, watch } from 'vue'
import { api, type MailAliasesView, type MailAliasRow, type MailAliasSignatureSave } from '@/api'
import MailAddressSignature from '@/components/MailAddressSignature.vue'

/**
 * Whose extra addresses: a member's (#422), on its page, or a person's (#886), on their entry under People. One of the
 * two ids is given; the same control manages either, over that holder's own routes.
 */
const props = defineProps<{ memberId?: string; personId?: string; editable: boolean }>()
const subjectId = computed(() => props.personId ?? props.memberId ?? '')
const who = computed(() => (props.personId ? 'this person' : 'this member'))
const client = computed(() =>
    props.personId
        ? {
              list: api.personMailAliases,
              add: api.addPersonMailAlias,
              update: api.updatePersonMailAlias,
              remove: api.removePersonMailAlias,
              saveSignature: api.savePersonMailAliasSignature,
              clearSignature: api.clearPersonMailAliasSignature,
          }
        : {
              list: api.mailAliases,
              add: api.addMailAlias,
              update: api.updateMailAlias,
              remove: api.removeMailAlias,
              saveSignature: api.saveMailAliasSignature,
              clearSignature: api.clearMailAliasSignature,
          },
)
const view = ref<MailAliasesView>({ aliases: [], domains: [] })
const localPart = ref('')
const domain = ref('')
const replyFromAlias = ref(true)
const busy = ref(false)
const error = ref('')
let generation = 0
watch(
    subjectId,
    async id => {
        const current = ++generation
        view.value = { aliases: [], domains: [] }
        error.value = ''
        localPart.value = ''
        busy.value = true
        try {
            const result = await client.value.list(id)
            if (current !== generation) return
            view.value = result
            domain.value = result.domains[0] ?? ''
        } catch {
            if (current === generation) error.value = 'Could not load email aliases.'
        } finally {
            if (current === generation) busy.value = false
        }
    },
    { immediate: true },
)

async function change(operation: () => Promise<MailAliasesView>): Promise<boolean> {
    if (busy.value || !props.editable) return false
    const current = generation
    busy.value = true
    error.value = ''
    try {
        const result = await operation()
        if (current !== generation) return false
        view.value = result
        return true
    } catch (caught) {
        if (current === generation) {
            const message = caught instanceof Error ? caught.message : ''
            if (message.includes('alias_address_taken')) error.value = 'That address is already in use.'
            else if (message.includes('use_mailbox_owner_handoff'))
                error.value = 'That is a member’s address. Use the mailbox owner control to hand it off.'
            else if (message.includes('alias_domain_inactive'))
                error.value = 'Verify domain ownership and choose receiving or migration on the Domains page.'
            else error.value = 'Could not save the alias. Check the address and try again.'
        }
        return false
    } finally {
        if (current === generation) busy.value = false
    }
}
function toggle(alias: MailAliasRow, event: Event): void {
    // Keep the stored choice visible until the server accepts the change, including on a failed request.
    ;(event.target as HTMLInputElement).checked = alias.replyFromAlias
    void change(() => client.value.update(subjectId.value, alias.id, !alias.replyFromAlias))
}
function remove(alias: MailAliasRow): void {
    void change(() => client.value.remove(subjectId.value, alias.id))
}
/**
 * The address's own signature (#1122), saved or removed: the row shows it once the server has it. A refusal is thrown
 * on, so the signature's own editor stays open and says what went wrong.
 */
async function signature(operation: () => Promise<MailAliasesView>): Promise<void> {
    const current = generation
    const result = await operation()
    if (current === generation) view.value = result
}
function saveSignature(alias: MailAliasRow, body: MailAliasSignatureSave): Promise<void> {
    return signature(() => client.value.saveSignature(subjectId.value, alias.id, body))
}
function clearSignature(alias: MailAliasRow): Promise<void> {
    return signature(() => client.value.clearSignature(subjectId.value, alias.id))
}
async function add(): Promise<void> {
    if (!localPart.value.trim() || !domain.value) return
    if (
        await change(() =>
            client.value.add(subjectId.value, `${localPart.value.trim()}@${domain.value}`, replyFromAlias.value),
        )
    ) {
        localPart.value = ''
        replyFromAlias.value = true
    }
}
</script>

<template>
    <ZhPanel class="space-y-3" data-testid="mail-aliases">
        <ZhSectionHeading>Email aliases</ZhSectionHeading>
        <p class="text-muted-foreground text-sm">Give {{ who }} additional email addresses. Choose which addresses replies use, and give an address its own signature.</p>
        <p class="text-muted-foreground text-xs">You can prepare aliases after verifying domain ownership. They start handling mail when the domain becomes active.</p>
        <p v-if="error" role="alert" class="text-destructive text-sm">{{ error }}</p>
        <ul v-if="view.aliases.length" class="space-y-3">
            <li v-for="alias in view.aliases" :key="alias.id" class="flex flex-wrap items-center gap-3">
                <span class="font-mono text-sm">{{ alias.address }}</span>
                <label class="flex items-center gap-2 text-sm">
                    <input type="checkbox" :checked="alias.replyFromAlias" :disabled="busy || !editable" :aria-label="`Reply from ${alias.address}`" @change="toggle(alias, $event)" />
                    Reply from this address
                </label>
                <Button v-if="editable" size="sm" variant="outline" :disabled="busy" :aria-label="`Remove ${alias.address}`" @click="remove(alias)">Remove</Button>
                <MailAddressSignature :alias="alias" :editable="editable" :save="body => saveSignature(alias, body)" :clear="() => clearSignature(alias)" />
            </li>
        </ul>
        <p v-else-if="!busy && !error" class="text-muted-foreground text-sm">No email aliases.</p>
        <form v-if="editable" class="space-y-3" data-testid="add-mail-alias" @submit.prevent="add">
            <div class="flex flex-wrap items-end gap-2">
                <div class="space-y-1">
                    <Label :for="`alias-local-${subjectId}`">Address</Label>
                    <Input :id="`alias-local-${subjectId}`" v-model="localPart" placeholder="hello" maxlength="64" required :disabled="busy" />
                </div>
                <span class="pb-2" aria-hidden="true">@</span>
                <div class="space-y-1">
                    <Label :for="`alias-domain-${subjectId}`">Domain</Label>
                    <select :id="`alias-domain-${subjectId}`" v-model="domain" class="border-input bg-background h-9 border px-3 text-sm" :disabled="busy" required>
                        <option v-for="host in view.domains" :key="host" :value="host">{{ host }}</option>
                    </select>
                </div>
                <Button type="submit" :disabled="busy || !domain || !localPart.trim()">Add alias</Button>
            </div>
            <label class="flex items-center gap-2 text-sm"><input v-model="replyFromAlias" type="checkbox" :disabled="busy" />Reply from this address</label>
            <p class="text-muted-foreground text-xs">Unchecked: replies use {{ who }}’s own email address.</p>
        </form>
        <p v-else class="text-muted-foreground text-xs">Only an enterprise owner can manage aliases.</p>
    </ZhPanel>
</template>
